<template>
  <v-app-bar :elevation="0" density="compact" class="standalone-topbar">
    <!-- ── Where am I ────────────────────────────────────────────────────
         The app's identity, at the far left of the bar — the first thing
         read on every screen, and the one place it appears (the sidebar
         below carries destinations only). The open section is the lit
         sidebar row; this bar never repeats it. -->
    <div class="sh-brand">
      <button
        v-if="mobile"
        type="button"
        class="sh-nav-toggle"
        :aria-expanded="navOpen ? 'true' : 'false'"
        aria-label="Toggle navigation"
        @click="toggleNav"
      >
        <v-icon size="20">{{ navOpen ? "close" : "menu" }}</v-icon>
      </button>
      <LogoTile :branding="branding" :alt="brandName" :size="26" />
      <span class="sh-brand__id">
        <span class="sh-brand__name">{{ brandName }}</span>
        <!-- PRODUCT apps carry the Metaship identity; the app itself (CRM,
             WMS) is the qualifier beside it. A white-label app has no
             subtitle — its name IS the brand. -->
        <span v-if="brandSubtitle" class="sh-brand__sub">{{ brandSubtitle }}</span>
      </span>
      <button
        v-if="!mobile"
        type="button"
        class="sh-collapse"
        :title="rail ? 'Expand sidebar' : 'Collapse sidebar'"
        :aria-label="rail ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="toggleRail"
      >
        <v-icon size="16">{{ rail ? "last_page" : "first_page" }}</v-icon>
      </button>
    </div>

    <v-spacer />

    <p v-if="switchError" class="standalone-topbar__error">{{ switchError }}</p>

    <v-menu v-if="showOrgSwitcher" location="bottom end">
      <template #activator="{ props }">
        <button type="button" class="standalone-topbar__chip standalone-topbar__org" v-bind="props">
          <LogoTile :src="currentOrgLogo" :alt="currentOrgName" :size="26" />
          <span class="standalone-topbar__chip-label">{{ currentOrgName }}</span>
          <v-icon size="14" class="standalone-topbar__chip-chev">unfold_more</v-icon>
        </button>
      </template>
      <div class="standalone-menu">
        <div class="standalone-menu__label">Switch organisation</div>
        <button
          v-for="org in session.accessibleOrgs"
          :key="org.id"
          type="button"
          class="sh-row"
          :class="{ 'sh-row--selected': org.id === session.currentOrg?.id }"
          :disabled="switching"
          @click="switchOrg(org)"
        >
          <LogoTile :src="org.logo || org.icon || null" :alt="org.name" :size="30" />
          <span class="sh-row__label">{{ org.name }}</span>
          <v-icon v-if="org.id === session.currentOrg?.id" size="16" color="primary">check</v-icon>
        </button>
      </div>
    </v-menu>
    <span v-else-if="session.currentOrg" class="standalone-topbar__chip standalone-topbar__org-static">
      <LogoTile :src="currentOrgLogo" :alt="currentOrgName" :size="26" />
      <span class="standalone-topbar__chip-label">{{ currentOrgName }}</span>
    </span>

    <v-btn
      icon
      variant="text"
      size="small"
      class="standalone-topbar__theme-toggle"
      :title="darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
      @click="toggleTheme"
    >
      <v-icon size="19">{{ darkMode ? "light_mode" : "dark_mode" }}</v-icon>
    </v-btn>

    <!-- ── Who am I ──────────────────────────────────────────────────────
         Name beside the avatar, not just an initial: a bare monogram is
         indistinguishable from the "?" fallback the shell used to show for
         every silently-resumed session (see stores/session.ts). -->
    <v-menu location="bottom end">
      <template #activator="{ props }">
        <button type="button" class="standalone-topbar__user-btn" v-bind="props">
          <span class="standalone-topbar__avatar">
            <img v-if="userAvatar" :src="userAvatar" :alt="userFullName" referrerpolicy="no-referrer" />
            <span v-else>{{ userInitials }}</span>
          </span>
          <span v-if="userFullName" class="standalone-topbar__user-name">{{ userFirstName }}</span>
          <v-icon size="14" class="standalone-topbar__chip-chev">unfold_more</v-icon>
        </button>
      </template>
      <div class="standalone-menu">
        <div class="standalone-menu__user">
          <span class="standalone-topbar__avatar standalone-topbar__avatar--lg">
            <img v-if="userAvatar" :src="userAvatar" :alt="userFullName" referrerpolicy="no-referrer" />
            <span v-else>{{ userInitials }}</span>
          </span>
          <span class="standalone-menu__user-id">
            <span class="standalone-menu__user-name">{{ userFullName || "Signed in" }}</span>
            <span class="standalone-menu__user-email">{{ session.user?.emailAddress }}</span>
          </span>
        </div>
        <div class="standalone-menu__divider"></div>
        <!-- Only while Chromium is holding an install prompt for this origin
             (src/pwa/install.ts) — an already-installed app, and every
             browser that does not fire the event, simply never shows it. -->
        <button v-if="canInstall" type="button" class="sh-row standalone-topbar__install" @click="install">
          <v-icon size="17" class="standalone-menu__row-icon">add_to_home_screen</v-icon>
          <span class="sh-row__label">Install app</span>
        </button>
        <button type="button" class="sh-row standalone-topbar__signout" @click="session.signOut()">
          <v-icon size="17" class="standalone-menu__row-icon">logout</v-icon>
          <span class="sh-row__label">Sign out</span>
        </button>
      </div>
    </v-menu>
  </v-app-bar>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useDisplay, useTheme } from "vuetify";
import { useSessionStore } from "@/stores/session";
import LogoTile from "./LogoTile.vue";
import { navOpen, rail, toggleNav, toggleRail } from "./navState";
import { canInstall, promptInstall } from "@/pwa/install";
import type { UserOrganisation } from "@/tenant/types";

export default defineComponent({
  name: "TopBar",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();
    const theme = useTheme();
    // `mobileBreakpoint: 768` — plugins/vuetify.ts.
    const { mobile } = useDisplay();
    const switching = ref(false);
    const switchError = ref<string | null>(null);

    const showOrgSwitcher = computed(
      () => session.tenant?.kind === "PRODUCT" && session.accessibleOrgs.length > 1,
    );
    const currentOrgName = computed(() => session.currentOrg?.name || "");
    const currentOrgLogo = computed(() => session.currentOrg?.logo || session.currentOrg?.icon || null);

    const branding = computed(() => session.tenant?.branding ?? null);
    /**
     * A PRODUCT app is one of OUR products on our own domain (crm.metaship.ai,
     * wms.metaship.ai) — it wears the Metaship name, with the product as the
     * qualifier. A WHITE_LABEL app is the customer's own brand and must never
     * show ours.
     */
    const isProduct = computed(() => session.tenant?.kind === "PRODUCT");
    const brandName = computed(() => (isProduct.value ? "Metaship" : session.tenant?.name || "Metaship"));
    const brandSubtitle = computed(() => (isProduct.value ? session.tenant?.name || null : null));

    const darkMode = computed(() => theme.global.current.value.dark);
    function toggleTheme(): void {
      // The single source of truth for dark/light — flips body[data-theme]
      // too, which some hosted modules key off directly.
      window.MetaShipGlobalState?.toggleTheme();
      const isDark = window.MetaShipGlobalState?.getState().darkMode ?? true;
      theme.change(isDark ? "dark" : "light");
    }

    async function switchOrg(org: UserOrganisation): Promise<void> {
      if (org.id === session.currentOrg?.id || switching.value) return;
      switching.value = true;
      switchError.value = null;
      try {
        await session.selectOrganisation(org);
      } catch (e) {
        switchError.value = e instanceof Error ? e.message : "Could not switch organisation — please try again.";
      } finally {
        switching.value = false;
      }
    }

    const userFullName = computed(() => {
      const u = session.user;
      if (!u) return "";
      return `${u.firstname || ""} ${u.surname || ""}`.trim() || u.emailAddress || "";
    });
    const userFirstName = computed(() => session.user?.firstname || userFullName.value.split(" ")[0] || "");
    const userAvatar = computed(() => session.user?.avatar || null);
    const userInitials = computed(() => {
      const u = session.user;
      if (!u) return "?";
      const first = (u.firstname || u.emailAddress || "?").charAt(0);
      const second = (u.surname || "").charAt(0);
      return `${first}${second}`.toUpperCase();
    });

    return {
      session,
      mobile,
      rail,
      navOpen,
      toggleRail,
      toggleNav,
      branding,
      brandName,
      brandSubtitle,
      showOrgSwitcher,
      currentOrgName,
      currentOrgLogo,
      switching,
      switchError,
      switchOrg,
      darkMode,
      toggleTheme,
      userFullName,
      userFirstName,
      userAvatar,
      userInitials,
      canInstall,
      install: promptInstall,
    };
  },
});
</script>

<style scoped>
/* Full-bleed: the brand block at the left now sits ABOVE the sidebar rather
   than beside it, so the bar no longer indents by the nav width (and no
   longer re-flows every time the rail collapses). */
.standalone-topbar {
  background: var(--sh-chrome-bg) !important;
  /* An inset shadow, NOT `border-bottom`: a border adds a 49th pixel, and the
     bar's height is a published contract (`--metaship-header-height`, 48px)
     that every hosted module pads itself by. A 1px drift there puts the top of
     each module's content under the bar. */
  box-shadow: inset 0 -1px 0 var(--sh-hairline) !important;
}
.standalone-topbar :deep(.v-toolbar__content) {
  padding-left: 10px;
  padding-right: 8px;
  gap: 6px;
}

/* ── Brand ─────────────────────────────────────────────────────────────── */
.sh-brand {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  flex: 0 1 auto;
}
.sh-brand__id {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sh-brand__name {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The product qualifier beside the brand — a hairline keeps the two readable
   as "Metaship" AND "CRM" rather than one run-on name. */
.sh-brand__sub {
  flex: 0 1 auto;
  min-width: 0;
  padding-left: 8px;
  border-left: 1px solid var(--sh-hairline);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--sh-text-60);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sh-collapse,
.sh-nav-toggle {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: var(--sh-radius);
  background: transparent;
  cursor: pointer;
  color: var(--sh-text-45);
  transition:
    background 150ms ease,
    color 150ms ease;
}
.sh-nav-toggle {
  /* The primary way into navigation on a phone — full-strength ink, and a
     touch target rather than a 26px chevron. */
  width: 36px;
  height: 36px;
  margin-left: -4px;
  color: var(--sh-text);
}
.sh-collapse:hover,
.sh-nav-toggle:hover {
  background: var(--sh-hover);
  color: var(--sh-text);
}

/* Quiet pill for the org + user activators — same hover strength as a nav row. */
.standalone-topbar__chip,
.standalone-topbar__user-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 8px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: var(--sh-text);
  font: inherit;
  cursor: pointer;
  transition: background 150ms ease;
}
.standalone-topbar__chip:hover,
.standalone-topbar__user-btn:hover {
  background: var(--sh-hover);
}
.standalone-topbar__org-static {
  cursor: default;
}
.standalone-topbar__org-static:hover {
  background: transparent;
}
.standalone-topbar__chip-label {
  font-size: 12px;
  font-weight: 600;
  max-width: 190px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-topbar__chip-chev {
  color: var(--sh-text-45) !important;
}

/* People are circles (brand marks are the white squares above). */
.standalone-topbar__avatar {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  background: rgb(var(--v-theme-secondary));
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.standalone-topbar__avatar--lg {
  width: 38px;
  height: 38px;
  font-size: 14px;
}
.standalone-topbar__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.standalone-topbar__user-name {
  font-size: 12px;
  font-weight: 600;
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.standalone-topbar__error {
  color: rgb(var(--v-theme-error));
  font-size: 11px;
  margin: 0 8px;
}

/* ── Narrow screens ─────────────────────────────────────────────────────
   Everything to the right of the brand sheds its label before the brand
   itself sheds anything: the account controls stay reachable as icons, and
   the one line that says which app this is survives to the smallest width. */
@media (max-width: 767.98px) {
  .standalone-topbar :deep(.v-toolbar__content) {
    padding-left: 8px;
    padding-right: 4px;
    gap: 2px;
  }
  .standalone-topbar__chip-label,
  .standalone-topbar__user-name {
    display: none;
  }
  .standalone-topbar__chip-chev {
    display: none;
  }
  .standalone-topbar__chip,
  .standalone-topbar__user-btn {
    padding: 0 6px;
  }
  .standalone-topbar__error {
    display: none;
  }
}
@media (max-width: 420px) {
  /* Below this the product qualifier and the brand word compete for the same
     ~90px. The MARK is the identity at this size; the name follows it. */
  .sh-brand__sub {
    display: none;
  }
  .sh-brand__name {
    max-width: 96px;
  }
}
</style>

<!-- Menus teleport to <body>, outside this component's scope — these two
     surfaces are styled globally instead. -->
<style>
.standalone-menu {
  min-width: 230px;
  padding: 6px;
  border-radius: 12px;
  background: rgb(var(--v-theme-card));
  border: 1px solid var(--sh-hairline);
  box-shadow: 0 18px 40px -20px rgba(0, 0, 0, 0.6);
}
.standalone-menu__label {
  padding: 5px 8px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
}
.standalone-menu__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
}
.standalone-menu__user-id {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}
.standalone-menu__user-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-menu__user-email {
  font-size: 11px;
  color: var(--sh-text-45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-menu__divider {
  height: 1px;
  margin: 5px 0;
  background: var(--sh-hairline);
}
.standalone-menu__row-icon {
  color: var(--sh-text-60) !important;
}
.standalone-menu .sh-row {
  height: 36px;
}
.standalone-menu .sh-row--selected {
  background: var(--sh-active);
}
</style>
